import { useIntl } from "react-intl";
import { useCallback, useState } from "react";

// Components
import { Box, Divider, InputAdornment, TextField, Typography } from "@mui/material";
import CustomIcon from "components/CustomIcon";
import { CircledButton } from "pages/ProjectDashboard";
import { FiltersPopover } from "../FiltersPopover";
import { SelectedDataList } from "../SelectedDataList";
import { DataList } from "../DataList";

// Enums
import icons from "enums/icons";

// Utils
import { differenceBy, keyBy, merge, values } from "lodash";

// Styles
import { Theme } from "@mui/material/styles";

// Types
import { TPerson, TPeopleData, IMultiFilters } from "../../BehaviouralComparison.types";
import { useAuth, useInputDebounce } from "utils/hooks";
import { BCIndividualsRefResponse, BCTeamsRefResponse, getPaceDashboardIndividualsRef } from "services";
import { TList } from "pages/BehavioralComparison/useBehaviouralComparison";

const IndividualsFilter = ({
    data,
    setData,
    handleSetResults,
    multiFilterData,
    setMultiFilterData,
    pagination,
}: {
    data: TPeopleData;
    setData: (data: TPeopleData, avoidFetching?: boolean) => void;
    handleSetResults: <T extends BCIndividualsRefResponse | BCTeamsRefResponse>(data: T, type: TList) => void;
    multiFilterData: IMultiFilters;
    setMultiFilterData: React.Dispatch<React.SetStateAction<IMultiFilters>>;
    pagination: React.ReactNode;
}) => {
    const intl = useIntl();

    const { loggedInUserData } = useAuth();
    const orgId = loggedInUserData.selectedOrg?.id;

    const isSomePeopleSelected = Boolean(
        data.data?.filter((person) => person.checked).length > 0 &&
            data.data?.filter((person) => person.checked).length < data.data?.length
    );
    const isAllPeopleSelected = data.data?.every((person) => person.checked);

    const [expandSelected, setExpandSelected] = useState<boolean>(false);

    const [inputNameValue, setInputNameValue] = useState<string>("");

    const hasResults = data.data?.length > 0;
    const hasNoResults = !data.data?.length && data.status === "done" && inputNameValue.trim() !== "";

    const [filtersAnchorEl, setFiltersAnchorEl] = useState<HTMLElement | null>(null);

    const handleOpenPopover = (event: React.MouseEvent<HTMLDivElement>) => {
        setFiltersAnchorEl(event.currentTarget);
    };

    const handleClosePopover = () => {
        setFiltersAnchorEl(null);
    };

    // --------------------------------------------------------------------------------------------
    // SELECT AND UNSELECT FUNCTIONS
    // --------------------------------------------------------------------------------------------
    const handleCheck = (event: React.ChangeEvent<HTMLInputElement>, id: number | string) => {
        const checked = event.target.checked;
        const newData = data.data.map((person) => (person.id === id ? { ...person, checked } : person));
        const checkedPeople = newData.filter((person) => person.checked);
        let newSelectedData = [] as TPerson[];

        if (checked) {
            newSelectedData = values(merge(keyBy(data.selected || [], "id"), keyBy(checkedPeople, "id")));
        } else {
            newSelectedData = data.selected?.filter((person) => person.id !== id);
        }

        setData(
            {
                ...data,
                data: newData,
                selected: newSelectedData,
            },
            !checked
        );
    };

    const handleCheckAll = (event: React.ChangeEvent<HTMLInputElement>) => {
        const checked = event.target.checked;
        const newData = data.data.map((person) => ({ ...person, checked }));
        const checkedPeople = newData.filter((person) => person.checked);

        const newSelected = checked
            ? values(merge(keyBy(data.selected || [], "id"), keyBy(checkedPeople, "id")))
            : differenceBy(data.selected, newData, "id");

        setData({
            ...data,
            data: newData,
            selected: newSelected,
        });
    };

    const handleDeleteSelected = (id: number | string) => {
        const newData = data.data.map((person) => (person.id === id ? { ...person, checked: false } : person));
        const selectedData = data.selected.filter((person) => person.id !== id);

        setData(
            {
                ...data,
                data: newData,
                selected: selectedData,
            },
            true
        );
    };

    const handleRemoveAllSelected = () => {
        const newData = data.data.map((person) => ({ ...person, checked: false }));

        setExpandSelected(false);
        setData(
            {
                ...data,
                data: newData,
                selected: [],
            },
            true
        );
    };

    // --------------------------------------------------------------------------------------------
    // FILTER BY NAME
    // --------------------------------------------------------------------------------------------
    const handleChangeFilters = useCallback(
        async (value: string) => {
            if (!orgId) return;
            setData({ ...data, status: "fetching" }, true);
            const results = await getPaceDashboardIndividualsRef({
                ...data.meta,
                filter_pace_only: true,
                filter_name: value,
                orgId,
            });
            handleSetResults<BCIndividualsRefResponse>(results.data, "individuals");
        },
        [data, handleSetResults, orgId, setData]
    );

    const handleFilterByName = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value.trim();
        setInputNameValue(value);
        delayedHandleInputChange(value);
    };

    const { delayedHandleInputChange } = useInputDebounce(handleChangeFilters);

    return (
        <>
            <Box sx={{ width: "100%", display: "flex", alignItems: "center" }}>
                <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    id="user-search"
                    name="user-search"
                    autoComplete="off"
                    value={inputNameValue}
                    sx={(theme: Theme) => ({
                        width: "100%",
                        marginTop: theme.spacing(1),
                        "& .MuiInputBase-input": {
                            height: 22,
                        },
                        "& .MuiInputAdornment-root .MuiSvgIcon-root": {
                            fontSize: 16,
                        },
                    })}
                    onChange={handleFilterByName}
                    placeholder={intl.formatMessage({
                        id: "bahaviouralComparison.filterByName",
                    })}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <CustomIcon icon={icons.searchInverted} />
                            </InputAdornment>
                        ),
                    }}
                />

                {/* <CircledButton
                    component="button"
                    size={32}
                    sx={{
                        marginLeft: "8px",
                        marginTop: "8px",
                        borderColor: "secondary.main",
                        "&:hover": {
                            borderColor: "secondary.main",
                        },
                    }}
                    onClick={handleOpenPopover}
                >
                    <CustomIcon icon={icons.filter} sx={{ fontSize: 15, color: "secondary.main" }} />
                </CircledButton> */}
            </Box>
            <FiltersPopover
                anchorEl={filtersAnchorEl}
                handleClose={handleClosePopover}
                multiFilterData={multiFilterData}
                setMultiFilterData={setMultiFilterData}
            />
            {hasResults && (
                <>
                    <SelectedDataList
                        type="users"
                        selectedItems={data.selected}
                        allSelected={isAllPeopleSelected}
                        someSelected={isSomePeopleSelected}
                        expandSelected={expandSelected}
                        setExpandSelected={setExpandSelected}
                        handleCheckAll={handleCheckAll}
                        handleRemoveAllSelected={handleRemoveAllSelected}
                        handleDeleteSelected={handleDeleteSelected}
                    />

                    <Divider sx={{ color: "neutrals.light", margin: "8px 0px 8px 0px" }} />

                    <DataList
                        id="people-list"
                        pagination={pagination}
                        type="user"
                        filteredData={data.data}
                        handleCheck={handleCheck}
                    />
                </>
            )}

            {hasNoResults && (
                <Box sx={{ height: 260, textAlign: "center", marginTop: 2 }}>
                    <Typography sx={{ fontSize: 16 }}>
                        {intl.formatMessage({ id: "behaviouralComparison.filterByName.noResults01" })}
                    </Typography>
                    <Typography sx={{ fontSeize: 14 }}>
                        {intl.formatMessage({ id: "behaviouralComparison.filterByName.noResults02" })}
                    </Typography>
                </Box>
            )}
        </>
    );
};

export default IndividualsFilter;
